import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Box,
  Button,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Collapse,
  TextField,
  InputAdornment,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import {
  Close as CloseIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';

const Logo = styled('img', {
  shouldForwardProp: (prop) => prop !== '$logoSize',
})(({ theme, $logoSize }) => ({
  width: $logoSize ? `${$logoSize}px` : '200px',
  height: 'auto',
  transition: 'transform 0.2s ease, width 0.2s ease',
  '&:hover': {
    transform: 'scale(1.02)',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '150px',
  },
}));

export default function Header({ config }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenuTitle, setActiveMenuTitle] = useState(null);
  const [dynamicMenus, setDynamicMenus] = useState([]);
  const [mobileSubMenuOpen, setMobileSubMenuOpen] = useState({});

  const menuThemeKey = config?.menuTheme || 'gov';
  const fontSize = isSmallScreen ? '0.85rem' : '0.92rem';
  const headerBorderColor = config?.headerBorderColor || '#EAB308';

  const headerBg = config?.isGradient
    ? `linear-gradient(135deg, ${config?.color1 || '#0C326F'} 0%, ${config?.color2 || '#1351B4'} 100%)`
    : (config?.color1 || '#0C326F');

  const getThemeStyles = () => {
    switch (menuThemeKey) {
      case 'modern_glass':
        return {
          appBar: {
            background: headerBg,
            backdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          },
          navBox: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginLeft: 3,
            gap: '6px',
          },
          itemBtn: {
            borderRadius: '20px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: fontSize,
            color: '#F8FAFC',
            px: 2,
            py: 0.75,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
            },
          },
          menuPaper: {
            background: config?.color3 || '#121212',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '12px',
            mt: 1,
          },
          menuItemColor: '#F1F5F9',
        };

      case 'minimal_clean':
        return {
          appBar: {
            background: headerBg,
            borderTop: `4px solid ${config?.color2 || '#3B82F6'}`,
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
          },
          navBox: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 1,
            flexWrap: 'wrap',
            gap: '4px',
          },
          itemBtn: {
            borderRadius: '4px',
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: fontSize,
            color: '#E2E8F0',
            px: 1.8,
            py: 0.8,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          },
          menuPaper: {
            background: config?.color3 || '#121212',
            borderTop: `2px solid ${config?.color2 || '#3B82F6'}`,
            borderRadius: '0 0 8px 8px',
          },
          menuItemColor: '#CBD5E1',
        };

      case 'gov':
      default:
        return {
          appBar: {
            background: headerBg,
            borderBottom: `4px solid ${headerBorderColor}`,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
          },
          navBox: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginLeft: 3,
            gap: '2px',
          },
          itemBtn: {
            borderRadius: '2px',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: fontSize,
            color: '#FFFFFF',
            px: 1.8,
            py: 1,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderBottom: `2px solid ${headerBorderColor}`,
            },
          },
          menuPaper: {
            background: '#FFFFFF',
            borderLeft: `4px solid ${config?.color1 || '#0C326F'}`,
            borderRadius: '2px',
          },
          menuItemColor: '#030a14',
        };
    }
  };

  const currentTheme = getThemeStyles();

  useEffect(() => {
    const menus = config?.menus || [];
    const normalizedMenus = menus.map((menu) => ({
      ...menu,
      items: Array.isArray(menu.items) ? menu.items : [],
      link: menu.link || '',
    }));
    setDynamicMenus(normalizedMenus);
  }, [config?.menus]);

  const toggleDrawer = (open) => () => setDrawerOpen(open);

  const handleOpenDesktopMenu = (event, title) => {
    setAnchorEl(event.currentTarget);
    setActiveMenuTitle(title);
  };

  const handleCloseDesktopMenu = () => {
    setAnchorEl(null);
    setActiveMenuTitle(null);
  };

  const toggleMobileSubMenu = (title) => {
    setMobileSubMenuOpen((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/busca?q=${encodeURIComponent(searchTerm.trim())}`);
      setSearchOpen(false);
      setSearchTerm('');
    }
  };

  const renderIcon = (iconName) => {
    if (iconName && MuiIcons[iconName]) {
      return React.createElement(MuiIcons[iconName]);
    }
    return null;
  };

  return (
    <>
      <AppBar position="sticky" sx={currentTheme.appBar}>
        <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isMobile && (
              <IconButton edge="start" color="inherit" onClick={toggleDrawer(true)} sx={{ mr: 1 }}>
                <MenuIcon sx={{ fontSize: 30 }} />
              </IconButton>
            )}

            <Link to={'/'} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', padding: 10 }}>
              <Logo
                src={config?.logo || 'https://kenekt-cdn.imgix.net/tvz5ih9ce0gbj652p4i5q2xpbq8u?ixlib=rails-4.3.1'}
                alt="Logo"
                $logoSize={config?.logoSize}
              />
            </Link>

            {!isMobile && (
              <Box sx={currentTheme.navBox}>
                <Link to={'/'} style={{ textDecoration: 'none' }}>
                  <Button color="inherit" startIcon={<HomeIcon />} sx={currentTheme.itemBtn}>
                    Início
                  </Button>
                </Link>

                {dynamicMenus.map((menu, index) =>
                  menu.title ? (
                    <React.Fragment key={index}>
                      {(menu.items || []).length > 0 ? (
                        <>
                          <Button
                            color="inherit"
                            startIcon={renderIcon(menu.icon)}
                            endIcon={<ExpandMoreIcon sx={{ fontSize: '18px !important' }} />}
                            onClick={(event) => handleOpenDesktopMenu(event, menu.title)}
                            sx={currentTheme.itemBtn}
                          >
                            {menu.title}
                          </Button>
                          <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl) && activeMenuTitle === menu.title}
                            onClose={handleCloseDesktopMenu}
                            TransitionComponent={Fade}
                            PaperProps={{ sx: currentTheme.menuPaper }}
                          >
                            {(menu.items || []).map((sub, sIdx) => (
                              <MenuItem
                                key={sIdx}
                                component={Link}
                                to={sub.link}
                                onClick={handleCloseDesktopMenu}
                                sx={{ color: currentTheme.menuItemColor, fontWeight: 500 }}
                              >
                                {sub.name}
                              </MenuItem>
                            ))}
                          </Menu>
                        </>
                      ) : (
                        <Button
                          color="inherit"
                          component={Link}
                          to={menu.link || '#'}
                          startIcon={renderIcon(menu.icon)}
                          sx={currentTheme.itemBtn}
                        >
                          {menu.title}
                        </Button>
                      )}
                    </React.Fragment>
                  ) : null
                )}
              </Box>
            )}
          </Box>

          <IconButton color="inherit" onClick={() => setSearchOpen(true)}>
            <SearchIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Drawer Navegação Mobile */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 280, bgcolor: '#121212', height: '100%', color: '#FFF' }} role="presentation">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #333' }}>
            <Typography variant="h6" fontWeight="bold">Menu</Typography>
            <IconButton onClick={toggleDrawer(false)} sx={{ color: '#FFF' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <List>
            <ListItem button component={Link} to="/" onClick={toggleDrawer(false)}>
              <ListItemIcon sx={{ color: '#FFF' }}><HomeIcon /></ListItemIcon>
              <ListItemText primary="Início" />
            </ListItem>
            <Divider sx={{ borderColor: '#333' }} />
            {dynamicMenus.map((menu, index) => (
              <React.Fragment key={index}>
                {menu.items && menu.items.length > 0 ? (
                  <>
                    <ListItem button onClick={() => toggleMobileSubMenu(menu.title)}>
                      <ListItemIcon sx={{ color: '#FFF' }}>{renderIcon(menu.icon)}</ListItemIcon>
                      <ListItemText primary={menu.title} />
                      {mobileSubMenuOpen[menu.title] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </ListItem>
                    <Collapse in={mobileSubMenuOpen[menu.title]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ bgcolor: '#1E1E1E' }}>
                        {menu.items.map((sub, sIdx) => (
                          <ListItem
                            key={sIdx}
                            button
                            component={Link}
                            to={sub.link}
                            onClick={toggleDrawer(false)}
                            sx={{ pl: 4 }}
                          >
                            <ListItemText primary={sub.name} />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </>
                ) : (
                  <ListItem button component={Link} to={menu.link || '#'} onClick={toggleDrawer(false)}>
                    <ListItemIcon sx={{ color: '#FFF' }}>{renderIcon(menu.icon)}</ListItemIcon>
                    <ListItemText primary={menu.title} />
                  </ListItem>
                )}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Diálogo de Pesquisa */}
      <Dialog open={searchOpen} onClose={() => setSearchOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: '#121212', color: '#FFF' }}>Pesquisar no Portal</DialogTitle>
        <DialogContent sx={{ bgcolor: '#121212' }}>
          <Box component="form" onSubmit={handleSearchSubmit} sx={{ pt: 1 }}>
            <TextField
              fullWidth
              autoFocus
              variant="outlined"
              placeholder="Digite o termo para busca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#FFF',
                  bgcolor: '#1E1E1E',
                  '& fieldset': { borderColor: '#333' },
                  '&:hover fieldset': { borderColor: '#555' },
                  '&.Mui-focused fieldset': { borderColor: '#0F71FD' },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton type="submit" sx={{ color: '#FFF' }}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}